// Import formatter
import CodeFormatter from '../components/CodeEditor/CodeFormatter';
// Import shared types
import type { SchemaField } from '../types/schemaMapperTypes';
import { create } from 'zustand';

// Adjust path if necessary

// --- Helper Functions (Ported from SchemaMapper.tsx) ---

// Use our improved CodeFormatter for formatting functions
const formatFunctionCode = (code: string): string => {
  if (!code.trim()) return code;
  try {
    // Use general-purpose formatter (assuming it handles passenger functions)
    return CodeFormatter.formatJavaScript(code);
  } catch (e) {
    console.error('Error in function formatter:', e);
    return code; // Return original code on error
  }
};

// Analyze a schema value to determine if it's a function and get its string representation
const analyzeSchemaValue = (
  value: unknown,
): { isFunction: boolean; valueAsString: string } => {
  let isFunction = false;
  let valueAsString = '';

  if (typeof value === 'string') {
    // Basic check for function-like syntax
    isFunction =
      value.includes('=>') ||
      value.includes('function(') ||
      value.includes('function (') ||
      (value.includes('(') &&
        value.includes(')') &&
        value.includes('{') &&
        value.includes('}'));
    valueAsString = value;
  } else if (typeof value === 'function') {
    isFunction = true;
    valueAsString = value.toString();
  } else {
    // For other types (objects, arrays, etc.), stringify them
    try {
      valueAsString = JSON.stringify(value);
    } catch {
      valueAsString = String(value); // Fallback
    }
  }

  return { isFunction, valueAsString };
};

// Merge a field with an existing field, preserving state
const mergeFieldWithExisting = (
  existingField: SchemaField,
  fieldName: string,
  fieldType: 'path' | 'function',
  formattedValue: string,
  pathValue: string,
  functionValue: string,
): SchemaField => {
  const mergedField: SchemaField = { ...existingField };

  // Update core properties
  mergedField.name = fieldName;
  mergedField.type = fieldType;
  mergedField.value = formattedValue; // Use formatted value for display

  // Intelligently merge type-specific values based on the *new* type
  if (fieldType === 'path') {
    // Update path value, preserve existing function value if any
    mergedField.pathValue = pathValue;
    // Keep existing function value: mergedField.functionValue = existingField.functionValue; (already covered by spread)
  } else {
    // fieldType === 'function'
    // Update function value, preserve existing path value if any
    mergedField.functionValue = functionValue;
    // Keep existing path value: mergedField.pathValue = existingField.pathValue; (already covered by spread)
  }
  // Ensure lastUpdated reflects potential changes
  mergedField.lastUpdated = Date.now();
  return mergedField;
};

// Create a completely new field
const createNewField = (
  fieldName: string,
  fieldType: 'path' | 'function',
  formattedValue: string,
  pathValue: string,
  functionValue: string,
): SchemaField => {
  return {
    name: fieldName,
    type: fieldType,
    value: formattedValue, // Use formatted value for display
    pathValue: pathValue,
    functionValue: functionValue,
    lastUpdated: Date.now(),
  };
};

// --- Zustand Store Definition ---

interface SchemaMapperState {
  fields: SchemaField[];
  fieldValidations: Record<string, boolean>;
  hasValidationErrors: boolean;
  // Actions
  initializeFields: (schema: Record<string, unknown>) => void;
  addField: () => void;
  removeField: (index: number) => void;
  updateField: (index: number, fieldUpdate: Partial<SchemaField>) => void;
  setFieldValidation: (fieldName: string, isValid: boolean) => void;
  updateOverallValidationStatus: () => void;
}

export const useSchemaMapperStore = create<SchemaMapperState>((set, get) => ({
  // Initial State
  fields: [],
  fieldValidations: {},
  hasValidationErrors: false,

  // --- Actions ---
  initializeFields: (schemaObject) => {
    console.log('Store: initializeFields called with schema:', schemaObject);
    const currentFields = get().fields;

    // Create a map of existing fields by name for efficient lookup
    const existingFieldsMap = new Map<string, SchemaField>();
    currentFields.forEach((field) => {
      if (field.name) {
        existingFieldsMap.set(field.name, { ...field }); // Deep copy
      }
    });

    // Process the schema object and create an array of field objects
    const processedFields: SchemaField[] = [];
    const defaultFunctionValue =
      '(input) => {\n  // Access input using dot notation, e.g: input.user.name\n  return input;\n}';
    const initialValidations: Record<string, boolean> = {};

    Object.entries(schemaObject).forEach(([key, value]) => {
      const existingField = existingFieldsMap.get(key);
      const { isFunction, valueAsString } = analyzeSchemaValue(value);
      const formattedValue = isFunction
        ? formatFunctionCode(valueAsString)
        : valueAsString;
      const fieldType = isFunction ? 'function' : 'path';
      const pathValue = isFunction
        ? existingField?.pathValue ?? ''
        : formattedValue; // Preserve old path if switching, else use formatted value
      const functionValue = isFunction
        ? formattedValue
        : existingField?.functionValue ?? defaultFunctionValue; // Preserve old func if switching, else use formatted value or default

      let finalField: SchemaField;
      if (existingField) {
        finalField = mergeFieldWithExisting(
          existingField,
          key,
          fieldType,
          formattedValue, // This might need adjustment depending on merge strategy
          pathValue,
          functionValue,
        );
        // If type changed, set value correctly based on new type
        finalField.value =
          fieldType === 'path'
            ? finalField.pathValue
            : finalField.functionValue;
      } else {
        finalField = createNewField(
          key,
          fieldType,
          fieldType === 'path' ? pathValue : functionValue, // Set initial value based on type
          pathValue,
          functionValue,
        );
      }
      processedFields.push(finalField);
      initialValidations[key] = true; // Assume valid initially
    });

    // Since all initialValidations are set to true, hasValidationErrors should be false
    set({
      fields: processedFields,
      fieldValidations: initialValidations,
      hasValidationErrors: false, // Initially all validations are true
    });
    // No separate call to updateOverallValidationStatus
  },

  addField: () => {
    console.log('Store: addField called');
    const defaultFunctionValue =
      '(input) => {\n  // Access input using dot notation, e.g: input.user.name\n  return input;\n}';
    const newField: SchemaField = {
      name: '',
      type: 'path',
      value: '',
      pathValue: '',
      functionValue: defaultFunctionValue,
      lastUpdated: Date.now(),
    };
    set((state) => ({ fields: [...state.fields, newField] }));
    // No validation change needed until name is added and validation runs
  },

  removeField: (index) => {
    console.log(`Store: removeField called for index: ${index}`);
    set((state) => {
      const fieldToRemove = state.fields[index];
      const newFields = state.fields.filter((_, i) => i !== index);
      const newValidations = { ...state.fieldValidations };

      // Remove validation status for the removed field if it had a name
      if (
        fieldToRemove?.name &&
        newValidations[fieldToRemove.name] !== undefined
      ) {
        delete newValidations[fieldToRemove.name];
      }

      // Calculate hasValidationErrors directly
      const hasErrors = Object.values(newValidations).some(
        (isValid) => !isValid,
      );

      return {
        fields: newFields,
        fieldValidations: newValidations,
        hasValidationErrors: hasErrors,
      };
    });
    // No separate call to updateOverallValidationStatus
  },

  updateField: (index, fieldUpdate) => {
    console.log(
      `Store: updateField called for index: ${index} with update:`,
      fieldUpdate,
    );
    set((state) => {
      const newFields = [...state.fields];
      const originalField = newFields[index];
      if (!originalField) return {}; // Should not happen, but safeguard

      const updatedField = { ...originalField };
      let nameChanged = false;
      const originalName = originalField.name;

      // --- Ported Logic from processFieldUpdate ---

      // Case 1: Update the field name
      if (
        fieldUpdate.name !== undefined &&
        fieldUpdate.name !== updatedField.name
      ) {
        updatedField.name = fieldUpdate.name;
        nameChanged = true;
      }

      // Case 2: Handle type changes (toggling between path and function)
      if (fieldUpdate.type && fieldUpdate.type !== updatedField.type) {
        const newType = fieldUpdate.type;
        console.log(
          `Store: Switching type from ${updatedField.type} to ${newType} for field "${updatedField.name}"`,
        );

        if (updatedField.type === 'path' && newType === 'function') {
          // Store the current path value
          updatedField.pathValue = updatedField.value;
          updatedField.type = 'function';
          // Ensure functionValue is valid or default
          if (
            !updatedField.functionValue ||
            updatedField.functionValue.trim() === ''
          ) {
            const defaultFunctionValue =
              '(input) => {\n  // Access input using dot notation, e.g: input.user.name\n  return input;\n}';
            updatedField.functionValue = defaultFunctionValue;
          }
          // Set the display value to the function value
          updatedField.value = updatedField.functionValue;
        } else if (updatedField.type === 'function' && newType === 'path') {
          // Save the current editor value to functionValue
          updatedField.functionValue = updatedField.value;
          updatedField.type = 'path';
          // Set the display value to the path value
          updatedField.value = updatedField.pathValue || '';
        }
        updatedField.lastUpdated = Date.now();
      }
      // Case 3: Handle value updates (if type didn't change)
      else if (fieldUpdate.value !== undefined) {
        const newValue = fieldUpdate.value;
        if (updatedField.type === 'path') {
          updatedField.value = newValue;
          updatedField.pathValue = newValue;
        } else {
          // type === 'function'
          // Update both value (editor state) and functionValue (raw state)
          updatedField.value = newValue;
          updatedField.functionValue = newValue;
          // NOTE: Debounced formatting is omitted here, handled by component/caller if needed
        }
        updatedField.lastUpdated = Date.now();
      }
      // --- End Ported Logic ---

      newFields[index] = updatedField;

      // Handle validation state if name changed
      const newValidations = { ...state.fieldValidations };
      if (nameChanged) {
        // Remove old validation entry if name existed and was tracked
        if (originalName && newValidations[originalName] !== undefined) {
          delete newValidations[originalName];
        }
        // Add new entry, assume valid until explicitly validated
        if (
          updatedField.name &&
          newValidations[updatedField.name] === undefined
        ) {
          newValidations[updatedField.name] = true;
        }
      }

      // Calculate hasValidationErrors directly
      const hasErrors = Object.values(newValidations).some(
        (isValid) => !isValid,
      );

      return {
        fields: newFields,
        fieldValidations: newValidations,
        hasValidationErrors: hasErrors,
      };
    });
    // No separate call to updateOverallValidationStatus
  },

  setFieldValidation: (fieldName, isValid) => {
    // Only update if fieldName is valid and validation state changes
    if (!fieldName) return;
    console.log(
      `Store: setFieldValidation called for field: ${fieldName}, isValid: ${isValid}`,
    );

    const currentValue = get().fieldValidations[fieldName];
    // Only make updates if the value is actually changing
    if (currentValue === isValid) {
      return; // Exit early without any updates
    }

    // Update the validation status and hasValidationErrors in a single update
    set((state) => {
      const newValidations = {
        ...state.fieldValidations,
        [fieldName]: isValid,
      };
      // Calculate hasValidationErrors directly
      const hasErrors = Object.values(newValidations).some((valid) => !valid);

      return {
        fieldValidations: newValidations,
        hasValidationErrors: hasErrors,
      };
    });
    // No separate call to updateOverallValidationStatus
  },

  updateOverallValidationStatus: () => {
    console.log('Store: updateOverallValidationStatus called');
    set((state) => {
      // Check if any *tracked* field validation is false
      const hasErrors = Object.values(state.fieldValidations).some(
        (isValid) => !isValid,
      );
      if (state.hasValidationErrors === hasErrors) {
        return {}; // No change needed
      }
      console.log(`Store: Updating hasValidationErrors to ${hasErrors}`);
      return { hasValidationErrors: hasErrors };
    });
  },
}));

// Optional: Export the type for convenience
export type SchemaMapperStore = SchemaMapperState;
